/**
 * All-organizations (account BASE) + per-org OVERRIDE + EFFECTIVE-VIEW API
 * client — backs the Settings V3 "All organizations" surface.
 *
 * Mirrors apps/chaos-gateway/src/all-orgs-settings.ts (registered under the
 * already-auth-guarded /api/account/* and /api/orgs/* prefixes). The gateway
 * translates snake_case → camelCase, so this client surfaces idiomatic TS.
 *
 * THREE layers, exactly as the gateway exposes them:
 *   • BASE      — account-level "All organizations" defaults (constitution_base,
 *                 approval_rules_base, provider-tiers-base re-expose).
 *   • OVERRIDE  — per-org layer (provider_tier_overrides, constitution addendum).
 *   • EFFECTIVE — merged read for ONE org (base ⊕ that org's override). Inert /
 *                 additive — safe to call; never mutates.
 *
 * Write paths are flag-gated server-side (ALL_ORGS_SETTINGS_ENABLED). When the
 * gateway flag is OFF, writes return 403 'all_orgs_settings_disabled' — callers
 * surface that as a disabled affordance rather than an error toast. Reads are
 * never gated, so the V3 UI renders the effective view even when writes are off.
 */
import { apiJson } from './client';

// ── shapes (mirror the gateway row→API mappers) ────────────────────────
export interface ConstitutionBase {
  accountId: string;
  currentVersion: number;
  body: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface ApprovalRuleBase {
  id: string;
  accountId: string;
  trigger: string;
  decision: string;
  conditions: unknown;
  priority: number;
  active: boolean;
  createdBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface ProviderTierRow {
  provider: string;
  topTierModel: string | null;
  balancedTierModel: string | null;
  fastTierModel: string | null;
}

export interface ProviderTierOverride {
  orgId: string;
  provider: string;
  topTierModel: string | null;
  balancedTierModel: string | null;
  fastTierModel: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

// ── effective-view shapes (base ⊕ org) ─────────────────────────────────
export interface EffectiveConstitution {
  base: string | null;
  addendum: string | null;
  merged: string | null;
}

export interface EffectiveApprovalRule extends ApprovalRuleBase {
  layer: 'base' | 'org';
}

export interface EffectiveProviderTier {
  provider: string;
  tier: 'top' | 'balanced' | 'fast';
  model: string | null;
  hasOverride: boolean;
}

export interface EffectiveCatalogItem {
  id: string;
  name: string;
  granted: boolean;
  [k: string]: unknown;
}

// ── input shapes ───────────────────────────────────────────────────────
export interface UpdateConstitutionBaseInput {
  body: string;
}
export interface UpdateConstitutionAddendumInput {
  body: string;
  changeNote?: string;
}
export interface CreateApprovalRuleBaseInput {
  trigger: string;
  decision: string;
  conditions?: unknown;
  priority?: number;
  active?: boolean;
}
export type UpdateApprovalRuleBaseInput = Partial<CreateApprovalRuleBaseInput>;
export interface UpsertProviderTierOverrideInput {
  // null clears that tier (inherit global); omit keeps the existing override.
  topTierModel?: string | null;
  balancedTierModel?: string | null;
  fastTierModel?: string | null;
}

export const allOrgsSettingsApi = {
  // ── ACCOUNT BASE — constitution ──────────────────────────────────────
  getConstitutionBase(): Promise<ConstitutionBase | null> {
    return apiJson<ConstitutionBase | null>('/account/constitution-base');
  },
  putConstitutionBase(input: UpdateConstitutionBaseInput): Promise<ConstitutionBase> {
    return apiJson<ConstitutionBase>('/account/constitution-base', { method: 'PUT', json: input });
  },

  // ── ACCOUNT BASE — approval rules ────────────────────────────────────
  listApprovalRulesBase(params: { active?: boolean; trigger?: string } = {}): Promise<ApprovalRuleBase[]> {
    const usp = new URLSearchParams();
    if (params.active !== undefined) usp.set('active', String(params.active));
    if (params.trigger) usp.set('trigger', params.trigger);
    const q = usp.toString();
    return apiJson<ApprovalRuleBase[]>(`/account/approval-rules-base${q ? `?${q}` : ''}`);
  },
  createApprovalRuleBase(input: CreateApprovalRuleBaseInput): Promise<ApprovalRuleBase> {
    return apiJson<ApprovalRuleBase>('/account/approval-rules-base', { method: 'POST', json: input });
  },
  updateApprovalRuleBase(id: string, input: UpdateApprovalRuleBaseInput): Promise<ApprovalRuleBase> {
    return apiJson<ApprovalRuleBase>(`/account/approval-rules-base/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      json: input,
    });
  },
  deleteApprovalRuleBase(id: string): Promise<{ ok: boolean; id: string }> {
    return apiJson<{ ok: boolean; id: string }>(`/account/approval-rules-base/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  // ── ACCOUNT BASE — provider tiers (READ-ONLY re-expose of the global) ─
  getProviderTiersBase(): Promise<ProviderTierRow[]> {
    return apiJson<ProviderTierRow[]>('/account/provider-tiers-base');
  },

  // ── PER-ORG OVERRIDE — provider tiers ────────────────────────────────
  listProviderTierOverrides(orgId: string): Promise<ProviderTierOverride[]> {
    return apiJson<ProviderTierOverride[]>(`/orgs/${encodeURIComponent(orgId)}/provider-tier-overrides`);
  },
  putProviderTierOverride(
    orgId: string,
    provider: string,
    input: UpsertProviderTierOverrideInput,
  ): Promise<ProviderTierOverride> {
    return apiJson<ProviderTierOverride>(
      `/orgs/${encodeURIComponent(orgId)}/provider-tier-overrides/${encodeURIComponent(provider)}`,
      { method: 'PUT', json: input },
    );
  },
  deleteProviderTierOverride(orgId: string, provider: string): Promise<{ ok: boolean }> {
    return apiJson<{ ok: boolean }>(
      `/orgs/${encodeURIComponent(orgId)}/provider-tier-overrides/${encodeURIComponent(provider)}`,
      { method: 'DELETE' },
    );
  },

  // ── PER-ORG OVERRIDE — constitution addendum ─────────────────────────
  getConstitutionAddendum(orgId: string): Promise<{ body: string | null } | null> {
    return apiJson<{ body: string | null } | null>(`/orgs/${encodeURIComponent(orgId)}/constitution-addendum`);
  },
  putConstitutionAddendum(
    orgId: string,
    input: UpdateConstitutionAddendumInput,
  ): Promise<{ body: string | null }> {
    return apiJson<{ body: string | null }>(`/orgs/${encodeURIComponent(orgId)}/constitution-addendum`, {
      method: 'PUT',
      json: input,
    });
  },

  // ── EFFECTIVE-VIEW reads (base ⊕ org; inert, never gated) ─────────────
  // The gateway wraps every effective payload in an envelope
  // ({ base, addendum, merged } / { rules } / { rows } / { skills } / … ) and
  // returns the provider tiers as ONE row per provider with three model
  // columns. These helpers unwrap the envelope and flatten provider tiers to
  // the per-(provider×tier) shape the panels + the EffectiveProviderTier type
  // declare, so the consuming components receive the array/shape they expect.
  async effectiveConstitution(orgId: string): Promise<EffectiveConstitution> {
    const res = await apiJson<{
      base: { body?: string | null } | null;
      addendum: { body?: string | null } | null;
      merged: string | null;
    }>(`/orgs/${encodeURIComponent(orgId)}/effective/constitution`);
    return {
      base: res.base?.body ?? null,
      addendum: res.addendum?.body ?? null,
      merged: res.merged ?? null,
    };
  },
  async effectiveApprovalRules(orgId: string, includeInactive = false): Promise<EffectiveApprovalRule[]> {
    const q = includeInactive ? '?active=false' : '';
    const res = await apiJson<{ rules: EffectiveApprovalRule[] }>(
      `/orgs/${encodeURIComponent(orgId)}/effective/approval-rules${q}`,
    );
    return res.rules ?? [];
  },
  async effectiveProviderTiers(orgId: string): Promise<EffectiveProviderTier[]> {
    const res = await apiJson<{ rows: Array<ProviderTierRow & { hasOverride?: boolean }> }>(
      `/orgs/${encodeURIComponent(orgId)}/effective/provider-tiers`,
    );
    const out: EffectiveProviderTier[] = [];
    for (const r of res.rows ?? []) {
      const hasOverride = r.hasOverride === true;
      out.push({ provider: r.provider, tier: 'top', model: r.topTierModel ?? null, hasOverride });
      out.push({ provider: r.provider, tier: 'balanced', model: r.balancedTierModel ?? null, hasOverride });
      out.push({ provider: r.provider, tier: 'fast', model: r.fastTierModel ?? null, hasOverride });
    }
    return out;
  },
  effectiveSkills(orgId: string): Promise<EffectiveCatalogItem[]> {
    return fetchCatalog(`/orgs/${encodeURIComponent(orgId)}/effective/skills`, 'skills');
  },
  effectiveConnectors(orgId: string): Promise<EffectiveCatalogItem[]> {
    return fetchCatalog(`/orgs/${encodeURIComponent(orgId)}/effective/connectors`, 'connectors');
  },
  effectivePermissions(orgId: string): Promise<EffectiveCatalogItem[]> {
    return fetchCatalog(`/orgs/${encodeURIComponent(orgId)}/effective/permissions`, 'permissions');
  },
  effectiveTemplates(orgId: string): Promise<EffectiveCatalogItem[]> {
    return fetchCatalog(`/orgs/${encodeURIComponent(orgId)}/effective/templates`, 'templates');
  },
};

// Unwrap a catalog envelope ({ skills } / { connectors } / { permissions } /
// { templates }) and normalise each row to the panel's { id, name, granted }
// shape. The gateway uses `displayName` for the label and, for permissions,
// `permission` as the identity key (no `id`); normalise both here.
async function fetchCatalog(
  path: string,
  key: 'skills' | 'connectors' | 'permissions' | 'templates',
): Promise<EffectiveCatalogItem[]> {
  const res = await apiJson<Record<string, Array<Record<string, unknown>>>>(path);
  const rows = res[key] ?? [];
  return rows.map((r) => ({
    ...r,
    id: String(r.id ?? r.permission ?? r.slug ?? ''),
    name: String(r.displayName ?? r.name ?? r.slug ?? r.permission ?? ''),
    granted: r.granted === true,
  }));
}

// ── TanStack Query keys ────────────────────────────────────────────────
export const allOrgsSettingsKeys = {
  all: ['all-orgs-settings'] as const,
  constitutionBase: () => [...allOrgsSettingsKeys.all, 'constitution-base'] as const,
  approvalRulesBase: (params: { active?: boolean; trigger?: string }) =>
    [...allOrgsSettingsKeys.all, 'approval-rules-base', params] as const,
  providerTiersBase: () => [...allOrgsSettingsKeys.all, 'provider-tiers-base'] as const,
  providerTierOverrides: (orgId: string) =>
    [...allOrgsSettingsKeys.all, 'provider-tier-overrides', orgId] as const,
  constitutionAddendum: (orgId: string) =>
    [...allOrgsSettingsKeys.all, 'constitution-addendum', orgId] as const,
  effective: (orgId: string, section: string) =>
    [...allOrgsSettingsKeys.all, 'effective', section, orgId] as const,
};
